import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { format } from "date-fns";
import { useState } from "react";
import type { FC } from "react";

import { normalizeSupportTicketProductQuantity } from "./supportTicketProductLabel";
import { SupportTicketProductEditDialog } from "./SupportTicketProductEditDialog";

type Entry = {
  id: string;
  productName: string;
  productionDate: Date | null;
  serialNumber: string | null;
  quantity: number;
};

type ProductUpdate = {
  quantity: number;
  productionDate: Date | null;
  serialNumber: string | null;
};

type Props = {
  entries: Entry[];
  isDisabled?: boolean;
  onRemoveEntry: (id: string) => void;
  onUpdateEntry: (id: string, updates: ProductUpdate) => void;
};

const tableHeadings = ["Jednostka", "Ilość", "Data produkcji", "Numer seryjny"];

const tableColumns: TableHeadColumn<string>[] = tableHeadings.map((heading) => ({
  isSortable: false,
  label: heading,
}));

const formatProductionDate = (productionDate: Date | null): string =>
  productionDate ? format(productionDate, "dd.MM.yyyy") : "—";

const formatSerialNumber = (serialNumber: string | null): string => serialNumber?.trim() || "—";

export const SupportTicketProductsTable: FC<Props> = ({
  entries,
  isDisabled,
  onRemoveEntry,
  onUpdateEntry,
}) => {
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const editingEntry = entries.find((entry) => entry.id === editingEntryId) ?? null;

  const getTableRowCellsContent = (entry: Entry): RowCellContent[] => [
    {
      cellCss: { textWrap: "nowrap" },
      element: entry.productName,
    },
    {
      element: normalizeSupportTicketProductQuantity(entry.quantity).toFixed(0),
    },
    {
      element: formatProductionDate(entry.productionDate),
    },
    {
      element: formatSerialNumber(entry.serialNumber),
    },
  ];

  return (
    <>
      <div css={{ overflow: "auto", margin: "0 -0.5rem", padding: "0 0.5rem" }}>
        <Table columns={tableColumns}>
          <TableBody
            rows={entries.map((entry) => ({
              id: entry.id,
              cellsContent: getTableRowCellsContent(entry),
              onClick: isDisabled ? undefined : () => setEditingEntryId(entry.id),
              css: {
                cursor: isDisabled ? "default" : "pointer",
                "td:first-child": {
                  padding: "0.75rem",
                },
              },
            }))}
          />
        </Table>
      </div>
      {editingEntry && (
        <SupportTicketProductEditDialog
          key={editingEntry.id}
          productLabel={editingEntry.productName}
          productionDate={editingEntry.productionDate}
          serialNumber={editingEntry.serialNumber}
          quantity={editingEntry.quantity}
          isOpen
          onClose={() => setEditingEntryId(null)}
          onRemove={() => onRemoveEntry(editingEntry.id)}
          onUpdate={(updates) => onUpdateEntry(editingEntry.id, updates)}
        />
      )}
    </>
  );
};
